import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { backButton } from '@telegram-apps/sdk-react';

import { useDialogsStore } from '@/store/useDialogsStore.ts';
import { SystemModalContainer } from '@/components/common/SystemModal/SystemModal.tsx';
import { Cell } from '@/components/common/Cell/Cell.tsx';
import { useReportUnmatchAction } from '@/hooks/useReportUnmatchAction.tsx';
import { RelationKind, ReportReason } from '@/enums/apiEnums.ts';
import { routes } from '@/navigation/routes.tsx';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import { useSummariesStore } from '@/store/useSummariesStore.ts';

export const reasonToTranslationMap: Record<ReportReason, string> = {
  [ReportReason.INAPPROPRIATE_BEHAVIOR]: 'report.reason.inappropriate_behavior',
  [ReportReason.FAKE_PROFILE]: 'report.reason.fake_profile',
  [ReportReason.SPAM]: 'report.reason.spam',
  [ReportReason.HARASSMENT]: 'report.reason.harassment',
  [ReportReason.OFFENSIVE_CONTENT]: 'report.reason.offensive_content'
};

export const ReportModal: FC<{
  id: string;
  name: string;
  relationKind?: RelationKind;
  handleClose: () => void;
}> = ({ id, name, relationKind, handleClose }) => {
  const { t } = useTranslation();
  const { blockUserAndRemoveDialog } = useDialogsStore();
  const { blockAndRemoveCandidate } = useDiscoveryStore();
  const { getLikesCount } = useSummariesStore();

  const navigate = useNavigate();

  const [reason, setReason] = useState<ReportReason | null>(null);

  const handleBlock = async (id: string, reason: ReportReason) => {
    // report from chat or from full profile after header click in chat
    if (
      relationKind === RelationKind.KIND_MATCHED ||
      relationKind === RelationKind.KIND_FIRST_MOVE
    ) {
      await blockUserAndRemoveDialog(id, reason);
      backButton.hide();
      navigate(routes.messenger);
    }

    if (relationKind === RelationKind.KIND_LIKE) {
      await blockUserAndRemoveDialog(id, reason);
      backButton.hide();
      navigate(routes.search);
    }

    // report from full profile after click in search tab
    if (relationKind === RelationKind.KIND_UNSPECIFIED) {
      await blockAndRemoveCandidate(id, reason);
      // report can be done from likes list or search, not to complicate we will redirect to search for now
      backButton.hide();
      navigate(routes.search); // -1
    }

    // report from full profile after click in likes list
    if (relationKind === RelationKind.KIND_LIKES_YOU) {
      await blockAndRemoveCandidate(id, reason);
      void getLikesCount();
      // report can be done from likes list or search, not to complicate we will redirect to search for now
      backButton.hide();
      navigate(routes.search); // -1
    }
  };

  const openReportPopup = useReportUnmatchAction(
    id,
    name,
    reason || ReportReason.INAPPROPRIATE_BEHAVIOR,
    handleBlock
  );

  const handleOpenReportPopup = (selectedReason: ReportReason) => {
    if (selectedReason === reason) {
      setReason(null);
      setTimeout(() => setReason(selectedReason), 0);
    } else {
      setReason(selectedReason);
    }
  };

  const reportReasons: ReportReason[] = [
    ReportReason.INAPPROPRIATE_BEHAVIOR,
    ReportReason.FAKE_PROFILE,
    ReportReason.SPAM,
    ReportReason.HARASSMENT,
    ReportReason.OFFENSIVE_CONTENT
  ];

  useEffect(() => {
    if (reason) {
      void openReportPopup();
    }
  }, [reason]);

  return (
    <SystemModalContainer
      headerTitle={t('report_action')}
      handleClose={handleClose}
    >
      {reportReasons.map((reasonKey) => (
        <div
          key={reasonKey}
          onClick={() => handleOpenReportPopup(reasonKey)}
          style={{ paddingTop: 5, paddingBottom: 5 }}
        >
          <Cell>{t(reasonToTranslationMap[reasonKey])}</Cell>
        </div>
      ))}
    </SystemModalContainer>
  );
};
